import type {
  AccessSetting,
  AuthorProfile,
  ChannelAccessGrant,
  ChannelKind,
  ChatMessage,
  CommunityChat,
  CommunityForum,
  CommunityForumState,
  CommunitySidebarHeader,
  CommunityTopic,
  SectionStatus,
  SectionType,
  TopicReply,
  TopicStatus,
  TopicSummary,
  ViewAccessSetting,
} from '../components/Community/types';

export type ApiSection = {
  id: string;
  title: string;
  sort_order: number;
  status: SectionStatus;
  created_at: string;
  updated_at: string;
};

export type ApiAccessRules = {
  post_access: AccessSetting;
  create_topics_access: AccessSetting;
  reply_access: AccessSetting;
};

export type ApiChannel = {
  id: string;
  section_id: string;
  name: string;
  description: string;
  icon: string;
  channel_type: 'chat' | 'forum';
  category: 'information' | 'text';
  forum_type: SectionType | null;
  status: SectionStatus;
  access_mode: ViewAccessSetting;
  access_rules: ApiAccessRules;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ApiTopic = {
  id: string;
  channel_id: string;
  author: AuthorProfile;
  title: string;
  description: string;
  tags: string[];
  status: TopicStatus;
  has_summary: boolean;
  summary?: {
    body: string;
    prepared_by: 'moderator' | 'datacircle';
    updated_at: string;
  };
  reply_count: number;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
};

export type ApiReply = {
  id: string;
  topic_id: string;
  author: AuthorProfile;
  body: string;
  created_at: string;
  updated_at?: string | null;
};

export type ApiMessage = {
  id: string;
  channel_id: string;
  author: AuthorProfile;
  body: string;
  created_at: string;
  updated_at?: string | null;
};

export function mapSection(row: ApiSection): CommunitySidebarHeader {
  return {
    id: row.id,
    title: row.title,
    sortOrder: row.sort_order,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapForum(row: ApiChannel): CommunityForum {
  return {
    id: row.id,
    headerId: row.section_id,
    name: row.name,
    description: row.description,
    icon: row.icon || 'fa-folder',
    type: row.forum_type || 'regular',
    sortOrder: row.sort_order,
    status: row.status,
    createTopicsAccess: row.access_rules?.create_topics_access || 'all',
    replyAccess: row.access_rules?.reply_access || 'all',
    viewAccess: row.access_mode,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapChat(row: ApiChannel): CommunityChat {
  return {
    id: row.id,
    headerId: row.section_id,
    name: row.name,
    description: row.description,
    icon: row.icon || 'fa-comments',
    sortOrder: row.sort_order,
    status: row.status,
    postAccess: row.access_rules?.post_access || 'all',
    viewAccess: row.access_mode,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapTopic(row: ApiTopic): CommunityTopic {
  const summary: TopicSummary | undefined = row.summary
    ? {
        body: row.summary.body,
        preparedBy: row.summary.prepared_by,
        updatedAt: row.summary.updated_at,
      }
    : undefined;
  return {
    id: row.id,
    sectionId: row.channel_id,
    title: row.title,
    description: row.description,
    tags: row.tags || [],
    author: row.author,
    status: row.status,
    hasSummary: row.has_summary,
    summary,
    replyCount: row.reply_count,
    lastActivityAt: row.last_activity_at,
    createdAt: row.created_at,
  };
}

export function mapReply(row: ApiReply, authorKey?: string): TopicReply {
  return {
    id: row.id,
    topicId: row.topic_id,
    body: row.body,
    author: row.author,
    authorKey,
    createdAt: row.created_at,
    updatedAt: row.updated_at || undefined,
  };
}

export function mapMessage(row: ApiMessage, authorKey?: string): ChatMessage {
  return {
    id: row.id,
    chatId: row.channel_id,
    body: row.body,
    author: row.author,
    authorKey,
    createdAt: row.created_at,
    updatedAt: row.updated_at || undefined,
  };
}

export function emptyForumState(): CommunityForumState {
  return {
    headers: [],
    forums: [],
    chats: [],
    chatMessages: [],
    topics: [],
    replies: [],
    accessGrants: [],
  };
}

export function buildStateFromStructure(
  sections: ApiSection[],
  channels: ApiChannel[],
): Pick<CommunityForumState, 'headers' | 'forums' | 'chats'> {
  return {
    headers: sections.map(mapSection),
    forums: channels.filter((c) => c.channel_type === 'forum').map(mapForum),
    chats: channels.filter((c) => c.channel_type === 'chat').map(mapChat),
  };
}

export function grantToLocal(
  channelKind: ChannelKind,
  channelId: string,
  grant: { userId: string; email: string; label: string; grantedAt: string },
): ChannelAccessGrant {
  return {
    id: `grant_${channelKind}_${channelId}_${grant.userId}`,
    channelKind,
    channelId,
    userId: grant.userId,
    userEmail: grant.email,
    userLabel: grant.label,
    grantedAt: grant.grantedAt,
  };
}
